import { useMemo } from 'react';
import { useGlobalRankings } from './useGlobalRankings';

/**
 * Hook para obtener el mejor ranking de un jugador específico
 *
 * @param {number} playerId - ID del jugador
 * @returns {Object|null} Objeto con el tier y la categoría del mejor ranking, o null si no está en top 10
 * @returns {string} tier - Tipo de ranking: "gold" (top 1), "silver" (top 5), "bronze" (top 10), "red" (top 10 en bajadas de mercado)
 * @returns {string} category - Categoría del mejor ranking: "mercado", "fantasy", "mister", "marca", "biwenger"
 * @returns {string|null} marketType - Tipo de ranking de mercado: "up", "down", "value", o null si no es mercado
 */
export const usePlayerRankings = (playerId) => {
    return null;
    const { rankings } = useGlobalRankings();

    return useMemo(() => {
        if (!playerId || !rankings || !rankings[playerId]) {
            return null;
        }

        const playerRankings = rankings[playerId];

        // Definir rankings con su categoría y prioridad (orden: mercado, fantasy, mister, marca, biwenger)
        const rankingsWithCategory = [
            { rank: playerRankings.marketValueUp, category: 'mercado', marketType: 'up', priority: 1 },
            { rank: playerRankings.marketValue, category: 'mercado', marketType: 'value', priority: 1 },
            { rank: playerRankings.marketValueDown, category: 'mercado', marketType: 'down', priority: 1 },
            { rank: playerRankings.laLigaFantasy, category: 'fantasy', marketType: null, priority: 2 },
            { rank: playerRankings.misterFantasy, category: 'mister', marketType: null, priority: 3 },
            { rank: playerRankings.fantasyMarca, category: 'marca', marketType: null, priority: 4 },
            { rank: playerRankings.biwenger, category: 'biwenger', marketType: null, priority: 5 },
        ].filter((r) => r.rank != null && r.rank <= 10);

        if (rankingsWithCategory.length === 0) {
            return null;
        }

        // Encontrar el mejor ranking (menor número = mejor posición)
        const bestRankValue = Math.min(...rankingsWithCategory.map((r) => r.rank));

        // Si hay empate, usar el de menor prioridad (orden: mercado, fantasy, mister, marca, biwenger)
        const bestRankings = rankingsWithCategory.filter((r) => r.rank === bestRankValue);
        const bestRanking = bestRankings.sort((a, b) => a.priority - b.priority)[0];

        // Determinar el tier según el mejor ranking
        // Si es marketValueDown, usar "red" en lugar de gold/silver/bronze
        let tier;
        if (bestRanking.marketType === 'down') {
            tier = 'red'; // Bajadas de mercado siempre usan red
        } else if (bestRankValue === 1) {
            tier = 'gold'; // Top 1
        } else if (bestRankValue <= 5) {
            tier = 'silver'; // Top 5 (pero no top 1)
        } else if (bestRankValue <= 10) {
            tier = 'bronze'; // Top 10 (pero no top 1 ni top 5)
        } else {
            return null;
        }

        return {
            tier,
            category: bestRanking.category,
            marketType: bestRanking.marketType, // 'up', 'down', 'value', o null
        };
    }, [playerId, rankings]);
};
